import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  ChatbotActiveFilters,
  ChatbotFileEvent,
  ChatbotMessage,
  ChatbotMessageFilter,
  ChatbotRateLimit,
  ChatbotSessionRequest,
} from '@/models/chatbot';
import { parseStoppedContent } from '../stoppedResponse';
import { isChatbotActiveFilters } from '../utils/chatbotActiveFilters';

export interface ChatbotError {
  message: string;
  retryable: boolean;
}
interface ChatbotConversationState {
  messages: ChatbotMessage[];
}
interface ChatbotPersistedState {
  messages: ChatbotMessage[];
  rateLimit: ChatbotRateLimit;
  activeConversationId: string | null;
  pinnedFilters: ChatbotActiveFilters | null;
}
interface ChatbotTransientState {
  streamingContent: string;
  streamingFiles: ChatbotFileEvent[];
  streamingReasoningSteps: string[];
  streamingPromptFilters: ReadonlyArray<ChatbotMessageFilter>;
  isStreaming: boolean;
  isStopping: boolean;
  isDrawerOpen: boolean;
  error: ChatbotError | null;
  lastUserMessage: string | null;
  lastRequest: ChatbotSessionRequest | null;
  activeConversationId: string | null;
  activeSessionId: string | null;
}
interface ChatbotActions {
  addUserMessage: (content: string) => void;
  openDrawerWithUserMessage: (content: string) => void;
  setLastRequest: (request: ChatbotSessionRequest) => void;
  appendStreamingContent: (chunk: string) => void;
  addStreamingFile: (file: ChatbotFileEvent) => void;
  addStreamingReasoningStep: (step: string) => void;
  setStreamingPromptFilters: (filters: ReadonlyArray<ChatbotMessageFilter>) => void;
  resetStreamingState: () => void;
  finalizeAiMessage: (options?: { interrupted?: boolean }) => void;
  setStreaming: (streaming: boolean) => void;
  setStopping: (stopping: boolean) => void;
  setActiveSession: (session: { conversationId: string | null; sessionId: string | null }) => void;
  setError: (error: ChatbotError | null) => void;
  setDrawerOpen: (open: boolean) => void;
  updateRateLimit: (rateLimit: Partial<ChatbotRateLimit>) => void;
  setActiveConversationId: (conversationId: string | null) => void;
  setPinnedFilters: (pinnedFilters: ChatbotActiveFilters | null) => void;
  loadConversation: (
    conversationId: string,
    messages: ChatbotMessage[],
    pinnedFilters: ChatbotActiveFilters | null,
  ) => void;
  clearConversation: () => void;
  reset: () => void;
}
type ChatbotStore = ChatbotConversationState & ChatbotPersistedState & ChatbotTransientState & ChatbotActions;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isNullableNumber(value: unknown): value is number | null {
  return typeof value === 'number' || value === null;
}

function isNullableString(value: unknown): value is string | null {
  return typeof value === 'string' || value === null;
}

function isChatbotRateLimit(value: unknown): value is ChatbotRateLimit {
  return (
    isRecord(value) &&
    'remaining' in value &&
    'limit' in value &&
    'resetAt' in value &&
    isNullableNumber(value.remaining) &&
    isNullableNumber(value.limit) &&
    isNullableNumber(value.resetAt)
  );
}

function isChatbotFileEvent(value: unknown): value is ChatbotFileEvent {
  return (
    isRecord(value) &&
    value.type === 'file' &&
    typeof value.url === 'string' &&
    typeof value.filename === 'string' &&
    typeof value.size_bytes === 'number' &&
    (!('content_type' in value) || typeof value.content_type === 'string')
  );
}

function isChatbotMessageFilter(value: unknown): value is ChatbotMessageFilter {
  return (
    isRecord(value) &&
    typeof value.label === 'string' &&
    typeof value.value === 'string' &&
    (!('details' in value) || typeof value.details === 'string')
  );
}

function isChatbotMessage(value: unknown): value is ChatbotMessage {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    (value.role === 'user' || value.role === 'ai') &&
    typeof value.content === 'string' &&
    typeof value.timestamp === 'number' &&
    (!('files' in value) || (Array.isArray(value.files) && value.files.every(isChatbotFileEvent))) &&
    (!('reasoningSteps' in value) ||
      (Array.isArray(value.reasoningSteps) && value.reasoningSteps.every(step => typeof step === 'string'))) &&
    (!('promptFilters' in value) ||
      (Array.isArray(value.promptFilters) && value.promptFilters.every(isChatbotMessageFilter))) &&
    (!('interrupted' in value) || value.interrupted === 'live' || value.interrupted === 'history')
  );
}

function getChatbotPersistedState(value: unknown): Partial<ChatbotPersistedState> {
  if (!isRecord(value)) {
    return {};
  }

  return {
    ...('messages' in value && Array.isArray(value.messages) && value.messages.every(isChatbotMessage)
      ? { messages: value.messages }
      : {}),
    ...('rateLimit' in value && isChatbotRateLimit(value.rateLimit) ? { rateLimit: value.rateLimit } : {}),
    ...('activeConversationId' in value && isNullableString(value.activeConversationId)
      ? { activeConversationId: value.activeConversationId }
      : {}),
    ...('pinnedFilters' in value && (value.pinnedFilters === null || isChatbotActiveFilters(value.pinnedFilters))
      ? { pinnedFilters: value.pinnedFilters }
      : {}),
  };
}

type StreamingState = Pick<
  ChatbotTransientState,
  'streamingContent' | 'streamingFiles' | 'streamingReasoningSteps' | 'streamingPromptFilters'
>;

const RESET_STREAMING_STATE: StreamingState = {
  streamingContent: '',
  streamingFiles: [],
  streamingReasoningSteps: [],
  streamingPromptFilters: [],
};

const INITIAL_TRANSIENT_STATE: ChatbotTransientState = {
  ...RESET_STREAMING_STATE,
  isStreaming: false,
  isStopping: false,
  isDrawerOpen: false,
  error: null,
  lastUserMessage: null,
  lastRequest: null,
  activeConversationId: null,
  activeSessionId: null,
};

const INITIAL_CONVERSATION_STATE: ChatbotConversationState = { messages: [] };

const INITIAL_PERSISTED_STATE: ChatbotPersistedState = {
  messages: [],
  rateLimit: { remaining: null, limit: null, resetAt: null },
  activeConversationId: null,
  pinnedFilters: null,
};

export const STORAGE_KEY = 'ces-chatbot-state';
let nextMessageSequence = 0;

const createMessageId = (timestamp: number): string => `${timestamp}-${nextMessageSequence++}`;

const createChatbotMessage = (
  message: Omit<ChatbotMessage, 'id' | 'timestamp'> & Partial<Pick<ChatbotMessage, 'timestamp'>>,
): ChatbotMessage => {
  const timestamp = message.timestamp ?? Date.now();
  return { ...message, id: createMessageId(timestamp), timestamp };
};

const clearPersistedState = (): void => sessionStorage.removeItem(STORAGE_KEY);

const useChatbotStore = create<ChatbotStore>()(
  persist(
    set => ({
      ...INITIAL_CONVERSATION_STATE,
      ...INITIAL_PERSISTED_STATE,
      ...INITIAL_TRANSIENT_STATE,

      addUserMessage: (content: string) =>
        set(state => ({
          messages: [...state.messages, createChatbotMessage({ role: 'user', content })],
          error: null,
          lastUserMessage: content,
        })),

      openDrawerWithUserMessage: (content: string) =>
        set(state => ({
          isDrawerOpen: true,
          messages: [...state.messages, createChatbotMessage({ role: 'user', content })],
          error: null,
          lastUserMessage: content,
        })),

      setLastRequest: (request: ChatbotSessionRequest) => set({ lastRequest: request }),

      appendStreamingContent: (chunk: string) => set(state => ({ streamingContent: state.streamingContent + chunk })),

      addStreamingFile: file => set(state => ({ streamingFiles: [...state.streamingFiles, file] })),

      addStreamingReasoningStep: (step: string) =>
        set(state => ({ streamingReasoningSteps: [...state.streamingReasoningSteps, step] })),

      setStreamingPromptFilters: (filters: ReadonlyArray<ChatbotMessageFilter>) =>
        set({ streamingPromptFilters: filters }),

      resetStreamingState: () => set({ ...RESET_STREAMING_STATE }),

      finalizeAiMessage: options =>
        set(state => {
          const { content: streamedContent, interrupted: markerInterrupted } = parseStoppedContent(
            state.streamingContent,
          );
          const interrupted = (options?.interrupted ?? false) || markerInterrupted;
          const hasStreamedContent =
            Boolean(streamedContent) || state.streamingFiles.length > 0 || state.streamingReasoningSteps.length > 0;

          if (!hasStreamedContent && !interrupted) {
            return RESET_STREAMING_STATE;
          }

          return {
            messages: [
              ...state.messages,
              createChatbotMessage({
                role: 'ai',
                content: streamedContent,
                files: state.streamingFiles.length > 0 ? state.streamingFiles : undefined,
                promptFilters: state.streamingPromptFilters.length > 0 ? state.streamingPromptFilters : undefined,
                reasoningSteps: state.streamingReasoningSteps.length > 0 ? state.streamingReasoningSteps : undefined,
                ...(interrupted ? { interrupted: 'live' } : {}),
              }),
            ],
            ...RESET_STREAMING_STATE,
          };
        }),

      setStreaming: (streaming: boolean) => set({ isStreaming: streaming }),

      setStopping: (stopping: boolean) => set({ isStopping: stopping }),

      setActiveSession: ({ conversationId, sessionId }) =>
        set({ activeConversationId: conversationId, activeSessionId: sessionId }),

      setError: (error: ChatbotError | null) => set({ error }),

      setDrawerOpen: (open: boolean) => set({ isDrawerOpen: open }),

      updateRateLimit: (rateLimit: Partial<ChatbotRateLimit>) =>
        set(state => ({
          rateLimit: { ...state.rateLimit, ...rateLimit },
        })),

      setActiveConversationId: (conversationId: string | null) => set({ activeConversationId: conversationId }),

      setPinnedFilters: (pinnedFilters: ChatbotActiveFilters | null) => set({ pinnedFilters }),

      loadConversation: (
        conversationId: string,
        messages: ChatbotMessage[],
        pinnedFilters: ChatbotActiveFilters | null,
      ) =>
        set({
          messages,
          activeConversationId: conversationId,
          pinnedFilters,
          ...RESET_STREAMING_STATE,
          isStreaming: false,
          error: null,
          lastUserMessage: null,
          lastRequest: null,
        }),

      clearConversation: () => {
        set(state => ({
          messages: [],
          rateLimit: state.rateLimit,
          pinnedFilters: null,
          ...RESET_STREAMING_STATE,
          isStreaming: false,
          isStopping: false,
          isDrawerOpen: state.isDrawerOpen,
          error: null,
          lastUserMessage: null,
          lastRequest: null,
          activeConversationId: null,
          activeSessionId: null,
        }));
      },

      reset: () => {
        set({
          ...INITIAL_CONVERSATION_STATE,
          ...INITIAL_PERSISTED_STATE,
          ...INITIAL_TRANSIENT_STATE,
        });
        clearPersistedState();
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state): ChatbotPersistedState => ({
        messages: state.messages,
        rateLimit: state.rateLimit,
        activeConversationId: state.activeConversationId,
        pinnedFilters: state.pinnedFilters,
      }),
      merge: (persistedState, currentState) => ({
        ...currentState,
        ...getChatbotPersistedState(persistedState),
      }),
    },
  ),
);

export default useChatbotStore;
